/**
 * The curated medium → model-profile list. THE single place it lives.
 *
 * The completeness meter shows every artist a real, fully-completed profile in
 * their own medium, because a percentage tells an artist how far they are and
 * nothing about what "done" looks like. Adding or swapping an example is an edit
 * to `MODEL_PROFILES` below and nothing else — the profile page, the resolver and
 * the meter all read this map and none of them names a URL.
 *
 * Keys are medium `Category.slug` values (`painting`, `photography`, …), which is
 * what a profile's categories already carry. A child medium (`oil`, `watercolor`)
 * resolves through its parent slug, so the list only ever needs top-level media.
 *
 * The painter example is also the fallback, so the link is never absent and never
 * points at a page that does not exist. The photographer slot is outstanding
 * (issue #3347 open question) — until a URL is supplied, photographers see the
 * painter example rather than no example.
 *
 * The URLs are absolute and production, deliberately NOT derived from
 * `TOWN_SQUARE_URL`. These are specific real artists' profiles; a staging-scoped
 * URL would point at a row that stage's database does not have, which is exactly
 * the dead link the fallback exists to prevent.
 */

/** Curated, verified model profiles, keyed by medium category slug. */
export const MODEL_PROFILES = {
  painting: 'https://www.arthelper.com/havalina',
  // photography: pending — no curated photographer profile has been supplied yet.
} as const satisfies Record<string, string>;

/** Shown for any medium with no curated example, and for a profile with no medium at all. */
export const FALLBACK_MODEL_PROFILE_URL: string = MODEL_PROFILES.painting;

const CURATED: ReadonlyMap<string, string> = new Map(Object.entries(MODEL_PROFILES));

/** The minimum a category has to expose to be matched against the curated list. */
export type ExemplarCategory = {
  slug?: string | null;
  /** Slug of the parent medium, when the category is a child like `oil`. */
  parentSlug?: string | null;
  isPrimary?: boolean | null;
};

/**
 * Candidate slugs in match order: the primary medium first (with its parent as
 * the immediate next guess), then the remaining categories in the order given.
 * A profile's primary category is the one that drives its public label, so it is
 * the medium the artist would expect to be matched on.
 *
 * Exported because it is the only part of the match a test can actually pin while
 * `MODEL_PROFILES` holds a single entry: with painter as both the one curated
 * medium AND the fallback, every `modelProfileUrl` assertion returns the same
 * string whether the ordering works or not.
 */
export function exemplarCandidateSlugs(categories: ReadonlyArray<ExemplarCategory> | null | undefined): string[] {
  if (!categories?.length) return [];
  const ordered = [...categories].sort((a, b) => Number(b?.isPrimary === true) - Number(a?.isPrimary === true));
  return ordered.reduce<string[]>((memo, category) => {
    const slug = category?.slug;
    const parentSlug = category?.parentSlug;
    if (slug) {
      memo.push(slug);
    }
    if (parentSlug) {
      memo.push(parentSlug);
    }
    return memo;
  }, []);
}

/**
 * The model profile to show this artist. Always returns a URL — an unmatched
 * medium falls back to the painter example rather than dropping the link.
 */
export function modelProfileUrl(categories: ReadonlyArray<ExemplarCategory> | null | undefined): string {
  const match = exemplarCandidateSlugs(categories)
    .map((slug) => CURATED.get(slug))
    .find((url): url is string => url !== undefined);
  return match ?? FALLBACK_MODEL_PROFILE_URL;
}
